import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Plus, X, Layers, Zap, Trash2, Search } from 'lucide-react'
import { api } from '../lib/api'
import { formatCurrency } from '../lib/utils'
import { useAuth } from '../context/AuthContext'

// Bundle promotions (Phase A — admin CRUD).
//
// Two flavors under one page, filtered by tab:
//   • Fixed  — curated set of products at a single bundle price
//   • bxgy   — buy-X-get-Y with cart/category/brand/product scope
//
// Cart-application (Phase B) and customer-facing surface (Phase C)
// ship next. This page is the ops-side authoring tool.

type Kind = 'fixed' | 'bxgy'
type Scope = 'cart' | 'category' | 'brand' | 'product'
type RewardType = 'percent' | 'fixed' | 'free'

interface Bundle {
  id: string
  slug: string
  kind: Kind
  name_en: string
  name_ar: string
  description_en: string | null
  description_ar: string | null
  image_key: string | null
  bundle_price_fils: number | null
  scope: Scope | null
  scope_value: string | null
  trigger_qty: number | null
  reward_type: RewardType | null
  reward_value: number | null
  reward_qty: number | null
  active: number
  starts_at: string | null
  ends_at: string | null
  created_at: string
  updated_at: string
}

interface BundleItem {
  id: string
  product_id: string
  quantity: number
  sort_order: number
  sku: string | null
  name_en: string | null
  product_price_fils: number | null
  volume_ml: number | null
}

interface Product { id: string; sku: string; name: string; price_fils: number; volume_ml: number | null; category_name: string | null }
interface Category { id: string; name: string; slug: string }

export default function Bundles() {
  const { can } = useAuth()
  const canManage = can(['super_admin', 'operations', 'marketing'])
  const canDelete = can(['super_admin'])
  const qc = useQueryClient()
  const [tab, setTab] = useState<Kind>('fixed')
  const [activeFilter, setActiveFilter] = useState<'all' | 'active' | 'inactive'>('all')
  const [modal, setModal] = useState<Bundle | 'new' | null>(null)

  const params = new URLSearchParams({ kind: tab })
  if (activeFilter !== 'all') params.set('active', activeFilter)

  const { data, isLoading } = useQuery<{ bundles: Bundle[] }>({
    queryKey: ['bundles', tab, activeFilter],
    queryFn: () => api.get(`/bundles?${params}`).then(r => r.data),
  })

  const del = useMutation({
    mutationFn: (id: string) => api.delete(`/bundles/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['bundles'] }),
  })

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-lg font-semibold text-white flex items-center gap-2">
            <Layers size={20} /> Bundle promotions
          </h1>
          <p className="text-xs text-gray-500 mt-1">
            Fixed-price sets and buy-X-get-Y rules. The application engine is wired in and gated by the{' '}
            <code className="text-[#c41e3a]">store_settings.bundles_enabled</code> flag — flip to <code>'1'</code> when
            you're ready to go live (docs/admin/bundles.md). Bundles you seed here start applying the moment ops flips
            the flag; you can dry-run by creating a test bundle first with a short date window.
          </p>
        </div>
        {canManage && (
          <button
            onClick={() => setModal('new')}
            className="flex items-center gap-2 bg-[#C41E3A] hover:bg-[#a51830] text-white text-sm px-4 py-2 rounded-lg transition-colors"
          >
            <Plus size={15} /> New {tab === 'fixed' ? 'fixed bundle' : 'bxgy rule'}
          </button>
        )}
      </div>

      <div className="flex gap-1 mb-4 bg-white/5 rounded-xl p-1 w-fit">
        <TabButton current={tab} value="fixed" onClick={() => setTab('fixed')} icon={<Layers size={14} />} label="Fixed bundles" />
        <TabButton current={tab} value="bxgy"  onClick={() => setTab('bxgy')}  icon={<Zap size={14} />}    label="Buy-X-get-Y" />
      </div>

      <div className="flex gap-3 mb-5">
        <select
          value={activeFilter}
          onChange={e => setActiveFilter(e.target.value as 'all' | 'active' | 'inactive')}
          className="bg-[#1a1a1a] border border-[#2e2e2e] rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#C41E3A]"
        >
          <option value="all">All (active + inactive)</option>
          <option value="active">Active only</option>
          <option value="inactive">Inactive only</option>
        </select>
      </div>

      <div className="bg-[#1a1a1a] border border-[#2e2e2e] rounded-xl overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-sm text-gray-500">Loading…</div>
        ) : (data?.bundles ?? []).length === 0 ? (
          <div className="p-8 text-center text-sm text-gray-500">
            No {tab === 'fixed' ? 'fixed bundles' : 'bxgy rules'} yet.
            {canManage && ' Click "New" to create one.'}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs text-gray-600 border-b border-[#2e2e2e]">
                <th className="px-4 py-3 text-left font-medium">Name</th>
                <th className="px-4 py-3 text-left font-medium">Slug</th>
                {tab === 'fixed' ? (
                  <th className="px-4 py-3 text-left font-medium">Price</th>
                ) : (
                  <th className="px-4 py-3 text-left font-medium">Rule</th>
                )}
                <th className="px-4 py-3 text-left font-medium">Active</th>
                <th className="px-4 py-3 text-left font-medium">Window</th>
                <th className="px-4 py-3 w-8"></th>
              </tr>
            </thead>
            <tbody>
              {(data?.bundles ?? []).map(b => (
                <tr key={b.id} className="border-b border-[#222] hover:bg-white/[0.02] transition-colors cursor-pointer"
                    onClick={() => setModal(b)}>
                  <td className="px-4 py-3 text-white">
                    <div className="font-medium">{b.name_en}</div>
                    <div className="text-xs text-gray-500">{b.name_ar}</div>
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-400 font-mono">{b.slug}</td>
                  {tab === 'fixed' ? (
                    <td className="px-4 py-3 text-gray-300">{b.bundle_price_fils ? formatCurrency(b.bundle_price_fils) : '—'}</td>
                  ) : (
                    <td className="px-4 py-3 text-xs text-gray-300">{describeBxgy(b)}</td>
                  )}
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${b.active ? 'bg-green-500/15 text-green-400' : 'bg-gray-500/15 text-gray-400'}`}>
                      {b.active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-500">
                    {b.starts_at || b.ends_at
                      ? `${b.starts_at?.slice(0, 10) ?? '—'} → ${b.ends_at?.slice(0, 10) ?? '—'}`
                      : 'always'}
                  </td>
                  <td className="px-4 py-3">
                    {canDelete && (
                      <button
                        onClick={(e) => { e.stopPropagation(); if (confirm(`Delete "${b.name_en}"?`)) del.mutate(b.id) }}
                        className="text-gray-500 hover:text-red-400 transition-colors"
                        title="Delete (super_admin only)"
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {modal !== null && canManage && (
        <BundleModal
          bundle={modal === 'new' ? null : modal}
          defaultKind={tab}
          onClose={() => setModal(null)}
          onSaved={() => { qc.invalidateQueries({ queryKey: ['bundles'] }); setModal(null) }}
        />
      )}
    </div>
  )
}

function describeBxgy(b: Bundle): string {
  if (b.kind !== 'bxgy') return '—'
  const scopeText = b.scope === 'cart' ? 'any' : `${b.scope}=${b.scope_value ?? '?'}`
  const rewardText = b.reward_type === 'percent'
    ? `${b.reward_value}% off ${b.reward_qty}`
    : b.reward_type === 'fixed'
      ? `${((b.reward_value ?? 0) / 1000).toFixed(3)} JD off ${b.reward_qty}`
      : `${b.reward_qty} free`
  return `Buy ${b.trigger_qty} ${scopeText} → ${rewardText}`
}

function TabButton({ current, value, onClick, icon, label }: {
  current: string; value: string; onClick: () => void; icon: React.ReactNode; label: string
}) {
  const active = current === value
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
        active ? 'bg-[#C41E3A] text-white' : 'text-gray-400 hover:text-white'
      }`}
    >
      {icon} {label}
    </button>
  )
}

// ── Modal ────────────────────────────────────────────────────────────
function BundleModal({ bundle, defaultKind, onClose, onSaved }: {
  bundle: Bundle | null; defaultKind: Kind; onClose: () => void; onSaved: () => void
}) {
  const [kind] = useState<Kind>(bundle?.kind ?? defaultKind)
  const [slug, setSlug]                       = useState(bundle?.slug ?? '')
  const [nameEn, setNameEn]                   = useState(bundle?.name_en ?? '')
  const [nameAr, setNameAr]                   = useState(bundle?.name_ar ?? '')
  const [descriptionEn, setDescriptionEn]     = useState(bundle?.description_en ?? '')
  const [descriptionAr, setDescriptionAr]     = useState(bundle?.description_ar ?? '')
  const [active, setActive]                   = useState(bundle?.active !== 0)
  const [startsAt, setStartsAt]               = useState(bundle?.starts_at ?? '')
  const [endsAt, setEndsAt]                   = useState(bundle?.ends_at ?? '')

  // Fixed
  const [priceJd, setPriceJd] = useState(
    bundle?.kind === 'fixed' && bundle.bundle_price_fils
      ? (bundle.bundle_price_fils / 1000).toFixed(3)
      : ''
  )
  const [items, setItems] = useState<Array<{ product_id: string; quantity: number }>>([])

  // Load items only when editing an existing fixed bundle
  const { data: detail } = useQuery<{ bundle: Bundle; items: BundleItem[] }>({
    queryKey: ['bundle', bundle?.id],
    queryFn: () => api.get(`/bundles/${bundle!.id}`).then(r => r.data),
    enabled: !!bundle && kind === 'fixed',
  })
  useEffect(() => {
    if (detail && kind === 'fixed') {
      setItems(detail.items.map(it => ({ product_id: it.product_id, quantity: it.quantity })))
    }
  }, [detail, kind])

  // bxgy
  const [scope, setScope]                = useState<Scope>(bundle?.scope ?? 'cart')
  const [scopeValue, setScopeValue]      = useState(bundle?.scope_value ?? '')
  const [triggerQty, setTriggerQty]      = useState(String(bundle?.trigger_qty ?? 2))
  const [rewardType, setRewardType]      = useState<RewardType>(bundle?.reward_type ?? 'percent')
  const [rewardValue, setRewardValue]    = useState(String(bundle?.reward_value ?? 15))
  const [rewardQty, setRewardQty]        = useState(String(bundle?.reward_qty ?? 1))

  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function submit() {
    setBusy(true); setError(null)
    try {
      const body: Record<string, unknown> = {
        slug, name_en: nameEn, name_ar: nameAr,
        description_en: descriptionEn.trim() || null,
        description_ar: descriptionAr.trim() || null,
        active,
        starts_at: startsAt || null,
        ends_at: endsAt || null,
      }
      if (kind === 'fixed') {
        body.bundle_price_fils = Math.max(0, Math.round((parseFloat(priceJd) || 0) * 1000))
        body.items = items.filter(i => i.product_id && i.quantity > 0)
      } else {
        body.scope = scope
        body.scope_value = scope === 'cart' ? null : scopeValue
        body.trigger_qty = parseInt(triggerQty, 10) || 0
        body.reward_type = rewardType
        body.reward_value = parseInt(rewardValue, 10) || 0
        body.reward_qty = parseInt(rewardQty, 10) || 1
      }
      if (bundle) {
        await api.put(`/bundles/${bundle.id}`, body)
      } else {
        body.kind = kind
        await api.post('/bundles', body)
      }
      onSaved()
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { message?: string } } }).response?.data?.message
      setError(msg ?? 'Could not save. Please try again.')
      setBusy(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 px-4">
      <div className="bg-[#1a1a1a] border border-[#2e2e2e] rounded-xl w-full max-w-2xl max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#2e2e2e]">
          <h2 className="text-sm font-semibold text-white">
            {bundle ? 'Edit bundle' : `New ${kind === 'fixed' ? 'fixed bundle' : 'bxgy rule'}`}
          </h2>
          <button onClick={onClose}><X size={16} className="text-gray-400" /></button>
        </div>

        <div className="p-5 space-y-4">
          <FieldRow>
            <Field label="Name (EN)"><Input value={nameEn} onChange={setNameEn} placeholder="Party Pack" /></Field>
            <Field label="Name (AR)"><Input value={nameAr} onChange={setNameAr} placeholder="حزمة الحفلة" /></Field>
          </FieldRow>
          <Field label="Slug (URL-safe id)">
            <Input value={slug} onChange={setSlug} placeholder="party-pack" />
          </Field>
          <FieldRow>
            <Field label="Description (EN, optional)">
              <textarea value={descriptionEn} onChange={e => setDescriptionEn(e.target.value)} className={textareaCls} rows={2} />
            </Field>
            <Field label="Description (AR, optional)">
              <textarea value={descriptionAr} onChange={e => setDescriptionAr(e.target.value)} className={textareaCls} rows={2} />
            </Field>
          </FieldRow>

          {kind === 'fixed' ? (
            <>
              <Field label="Bundle price (JD)">
                <Input value={priceJd} onChange={setPriceJd} placeholder="89.000" type="number" />
              </Field>
              <FixedItemsEditor items={items} onChange={setItems} />
            </>
          ) : (
            <BxgyRuleEditor
              scope={scope}       onScope={setScope}
              scopeValue={scopeValue} onScopeValue={setScopeValue}
              triggerQty={triggerQty} onTriggerQty={setTriggerQty}
              rewardType={rewardType} onRewardType={setRewardType}
              rewardValue={rewardValue} onRewardValue={setRewardValue}
              rewardQty={rewardQty} onRewardQty={setRewardQty}
            />
          )}

          <FieldRow>
            <Field label="Starts (optional)"><Input type="date" value={startsAt.slice(0, 10)} onChange={setStartsAt} /></Field>
            <Field label="Ends (optional)"><Input type="date" value={endsAt.slice(0, 10)} onChange={setEndsAt} /></Field>
          </FieldRow>
          <label className="flex items-center gap-2 text-sm text-gray-300">
            <input type="checkbox" checked={active} onChange={e => setActive(e.target.checked)} className="accent-[#C41E3A]" />
            Active
          </label>

          {error && <div className="rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-300">{error}</div>}

          <div className="flex justify-end gap-3 pt-2">
            <button onClick={onClose} className="px-4 py-2 text-sm text-gray-400 hover:text-white">Cancel</button>
            <button onClick={submit} disabled={busy}
              className="px-4 py-2 bg-[#C41E3A] hover:bg-[#a51830] disabled:opacity-50 text-white text-sm rounded-lg">
              {busy ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// ── Fixed-bundle items editor ────────────────────────────────────────
function FixedItemsEditor({ items, onChange }: {
  items: Array<{ product_id: string; quantity: number }>
  onChange: (v: Array<{ product_id: string; quantity: number }>) => void
}) {
  const [search, setSearch] = useState('')
  const { data } = useQuery<{ products: Product[] }>({
    queryKey: ['products-lite', search],
    queryFn: () => api.get(`/products?search=${encodeURIComponent(search)}&limit=15`).then(r => r.data),
    enabled: search.length >= 2,
  })

  const productMap = useMemo(() => {
    const m = new Map<string, Product>()
    for (const p of data?.products ?? []) m.set(p.id, p)
    return m
  }, [data])

  return (
    <div className="border border-[#2e2e2e] rounded-lg p-3 space-y-3">
      <div className="text-xs font-medium text-gray-400 mb-1">Bundle contents (min 2 items)</div>
      {items.map((it, i) => (
        <div key={i} className="flex items-center gap-2">
          <div className="flex-1 text-xs text-gray-300 font-mono truncate">
            {productMap.get(it.product_id)?.name ?? it.product_id}
          </div>
          <input
            type="number" min="1" value={it.quantity}
            onChange={e => {
              const next = [...items]; next[i] = { ...it, quantity: Math.max(1, parseInt(e.target.value, 10) || 1) }
              onChange(next)
            }}
            className="w-16 bg-[#242424] border border-[#3a3a3a] rounded px-2 py-1 text-sm text-white"
          />
          <button onClick={() => onChange(items.filter((_, j) => j !== i))} className="text-gray-500 hover:text-red-400">
            <X size={14} />
          </button>
        </div>
      ))}
      <div className="relative">
        <div className="flex items-center gap-2 bg-[#242424] border border-[#3a3a3a] rounded-lg px-3">
          <Search size={13} className="text-gray-500" />
          <input
            value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search products to add (name or SKU)"
            className="flex-1 bg-transparent py-2 text-sm text-white focus:outline-none"
          />
        </div>
        {search.length >= 2 && (data?.products ?? []).length > 0 && (
          <div className="absolute left-0 right-0 top-full mt-1 bg-[#1a1a1a] border border-[#2e2e2e] rounded-lg max-h-56 overflow-y-auto z-10">
            {(data?.products ?? []).map(p => (
              <button
                key={p.id}
                onClick={() => {
                  if (items.some(it => it.product_id === p.id)) return
                  onChange([...items, { product_id: p.id, quantity: 1 }]); setSearch('')
                }}
                className="w-full text-left px-3 py-2 text-xs hover:bg-white/5 border-b border-[#2e2e2e] last:border-0"
              >
                <div className="text-white">{p.name}</div>
                <div className="text-gray-500 font-mono">{p.sku} · {formatCurrency(p.price_fils)}</div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

// ── bxgy rule editor ─────────────────────────────────────────────────
function BxgyRuleEditor(props: {
  scope: Scope; onScope: (v: Scope) => void
  scopeValue: string; onScopeValue: (v: string) => void
  triggerQty: string; onTriggerQty: (v: string) => void
  rewardType: RewardType; onRewardType: (v: RewardType) => void
  rewardValue: string; onRewardValue: (v: string) => void
  rewardQty: string; onRewardQty: (v: string) => void
}) {
  const { data: cats } = useQuery<{ categories: Category[] }>({
    queryKey: ['categories'], queryFn: () => api.get('/categories').then(r => r.data),
    enabled: props.scope === 'category',
  })

  return (
    <div className="border border-[#2e2e2e] rounded-lg p-3 space-y-3">
      <div className="text-xs font-medium text-gray-400 mb-1">Rule</div>
      <FieldRow>
        <Field label="Scope">
          <Select value={props.scope} onChange={v => { props.onScope(v as Scope); props.onScopeValue('') }}
            options={[
              { value: 'cart',     label: 'Any item in the cart' },
              { value: 'category', label: 'Products in a category' },
              { value: 'brand',    label: 'Products of a brand' },
              { value: 'product',  label: 'A specific product' },
            ]}
          />
        </Field>
        {props.scope !== 'cart' && (
          <Field label={props.scope === 'category' ? 'Category' : props.scope === 'brand' ? 'Brand text' : 'Product ID'}>
            {props.scope === 'category' ? (
              <Select
                value={props.scopeValue} onChange={props.onScopeValue}
                options={[{ value: '', label: 'Pick a category…' }, ...(cats?.categories ?? []).slice().sort((a, b) => a.name.localeCompare(b.name)).map(c => ({ value: c.id, label: c.name }))]}
              />
            ) : (
              <Input value={props.scopeValue} onChange={props.onScopeValue} placeholder={props.scope === 'brand' ? 'Johnnie Walker' : 'product uuid'} />
            )}
          </Field>
        )}
      </FieldRow>
      <FieldRow>
        <Field label="Trigger qty (buy X)">
          <Input value={props.triggerQty} onChange={props.onTriggerQty} type="number" />
        </Field>
        <Field label="Reward qty (get Y)">
          <Input value={props.rewardQty} onChange={props.onRewardQty} type="number" />
        </Field>
      </FieldRow>
      <FieldRow>
        <Field label="Reward type">
          <Select value={props.rewardType} onChange={v => props.onRewardType(v as RewardType)}
            options={[
              { value: 'percent', label: '% off' },
              { value: 'fixed',   label: 'JD off (in fils)' },
              { value: 'free',    label: 'Free (100% off)' },
            ]}
          />
        </Field>
        {props.rewardType !== 'free' && (
          <Field label={props.rewardType === 'percent' ? 'Percent (1–100)' : 'Fils (5000 = JD 5.000)'}>
            <Input value={props.rewardValue} onChange={props.onRewardValue} type="number" />
          </Field>
        )}
      </FieldRow>
      <p className="text-xs text-gray-500">
        Preview: {previewBxgy(props)}
      </p>
    </div>
  )
}

function previewBxgy(p: {
  scope: Scope; scopeValue: string; triggerQty: string; rewardType: RewardType; rewardValue: string; rewardQty: string
}): string {
  const scopeText = p.scope === 'cart' ? 'any items' : `${p.scope} items${p.scopeValue ? ` (${p.scopeValue})` : ''}`
  const reward = p.rewardType === 'percent'
    ? `${p.rewardValue}% off ${p.rewardQty} of them`
    : p.rewardType === 'fixed'
      ? `${(parseInt(p.rewardValue, 10) / 1000 || 0).toFixed(3)} JD off ${p.rewardQty}`
      : `${p.rewardQty} free`
  return `Buy ${p.triggerQty} ${scopeText}, get ${reward}.`
}

// ── Small building blocks ────────────────────────────────────────────
const inputCls = 'w-full bg-[#242424] border border-[#3a3a3a] rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#c41e3a]'
const textareaCls = inputCls + ' resize-none'

function FieldRow({ children }: { children: React.ReactNode }) {
  return <div className="grid grid-cols-1 md:grid-cols-2 gap-3">{children}</div>
}
function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-xs text-gray-400 mb-1">{label}</label>
      {children}
    </div>
  )
}
function Input({ value, onChange, placeholder, type = 'text' }: {
  value: string; onChange: (v: string) => void; placeholder?: string; type?: string
}) {
  return <input value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} type={type} className={inputCls} />
}
function Select({ value, onChange, options }: {
  value: string; onChange: (v: string) => void; options: { value: string; label: string }[]
}) {
  return (
    <select value={value} onChange={e => onChange(e.target.value)} className={inputCls}>
      {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  )
}
